/* femme Appstle migration core V3.10 | 2026-10-26
   Appstle-first bridge. New storefront subscriptions are restricted to the
   Multi Choice Plan group; legacy Appstle/Seal groups are ignored.
   V3.5 removes the temporary cadence DOM/CSS neutraliser. Cadence controls are
   now built correctly at source by the product UI runtime.
   V3.6 prevented stale native Appstle plans leaking into one-time purchases.
   V3.7 preserves Appstle's native checked state and synchronises it to the
   exact femme-selected plan whenever subscription is active.
   V3.8 removes premature pointerdown syncing from purchase-option controls;
   native state is synchronised only once the chosen state is authoritative or
   immediately before add-to-cart submission.
   V3.9 synchronises Appstle's hidden native selling-plan radio state with the
   visible femme choice. This preserves subscription-first merchandising while
   making an explicit one-time customer choice authoritative to Appstle's
   add.js interceptor.
   V3.10 keeps the successful native one-time bridge, but also synchronises the
   exact Appstle cadence control used by newer widget layouts. This prevents
   Appstle's interceptor falling back to the first plan (Every 2 weeks) after a
   customer explicitly chooses another subscription frequency.
*/
(() => {
  'use strict';

  const A = window.FemmeAppstleMigration = window.FemmeAppstleMigration || {};

  A.APP_ID = 'appstle';
  A.TARGET_GROUP_ID = '100555522435';
  A.TARGET_GROUP_NAME = 'Multi Choice Plan';
  A.DEFAULT_PLAN_ID = '713609675139';
  A.PLAN_LABELS = {
    '713609642371': 'Every 2 weeks',
    '713609675139': 'Every month',
    '713609707907': 'Every 2 months',
    '713609740675': 'Every 3 months'
  };

  A.APPSTLE_SELECTOR = [
    '[id^="appstle_subscription_widget"]',
    '[id*="appstle_subscription_widget"]',
    '.appstle_subscription_wrapper',
    '.appstle-subscription-widget',
    '[data-appstle-subscription-widget]',
    '[class*="appstle_subscription"]',
    '[class*="appstle-subscription"]'
  ].join(',');

  A.cache = A.cache || new Map();
  A.state = A.state || {
    controller: null,
    root: null,
    config: null,
    product: null,
    variants: new Map(),
    ui: null,
    selectedPlanId: '',
    selectionType: 'subscription',
    boundForms: new Set(),
    observer: null,
    raf: null
  };

  A.isAppstleControl = (control) =>
    control instanceof Element && Boolean(control.closest(A.APPSTLE_SELECTOR));

  A.isAppstleSellingPlanRadio = (control) => {
    if (!(control instanceof HTMLInputElement)) return false;
    if (String(control.type || '').toLowerCase() !== 'radio') return false;
    if (control.name !== 'selling_plan') return false;
    return A.isAppstleControl(control);
  };

  A.extractKnownPlanId = (value) => {
    const raw = String(value || '');
    const normalised = A.normaliseId(raw);
    if (A.PLAN_LABELS[normalised]) return normalised;

    return (
      Object.keys(A.PLAN_LABELS).find((planId) => raw.includes(planId)) || ''
    );
  };

  A.planTextMatches = (text, planId) => {
    const value = String(text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    if (!value) return false;

    switch (A.normaliseId(planId)) {
      case '713609642371':
        return (
          /\bfortnight(?:ly)?\b/.test(value) ||
          /\bevery\s+2\s+weeks?\b/.test(value) ||
          /\b2[\s-]*weekly\b/.test(value) ||
          /^2\s+weeks?$/.test(value)
        );
      case '713609675139':
        return (
          /\bevery\s+(?:1\s+)?month\b/.test(value) ||
          /\b1\s+monthly\b/.test(value) ||
          /^1\s+month$/.test(value) ||
          /^monthly(?:\s+subscription)?$/.test(value)
        );
      case '713609707907':
        return (
          /\bevery\s+2\s+months?\b/.test(value) ||
          /\b2\s+monthly\b/.test(value) ||
          /^2\s+months?$/.test(value)
        );
      case '713609740675':
        return (
          /\bevery\s+3\s+months?\b/.test(value) ||
          /\b3\s+monthly\b/.test(value) ||
          /^3\s+months?$/.test(value)
        );
      default:
        return false;
    }
  };

  A.controlLabelText = (control) => {
    if (!(control instanceof Element)) return '';

    const root = A.state?.root;
    const id = String(control.id || '');
    if (id && root) {
      try {
        const label = root.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (label) return String(label.textContent || '');
      } catch (_) {}
    }

    return String(control.closest('label')?.textContent || '');
  };

  A.controlMatchesPlan = (control, planId) => {
    if (!(control instanceof Element)) return false;

    const signals = [
      control.getAttribute('value'),
      control.id,
      ...Object.values(control.dataset || {})
    ];

    if (
      signals.some(
        (signal) => A.extractKnownPlanId(signal) === A.normaliseId(planId)
      )
    ) {
      return true;
    }

    return A.planTextMatches(A.controlLabelText(control), planId);
  };

  A.optionMatchesPlan = (option, planId) => {
    if (!(option instanceof HTMLOptionElement)) return false;

    const signals = [
      option.value,
      option.id,
      ...Object.values(option.dataset || {})
    ];

    if (
      signals.some(
        (signal) => A.extractKnownPlanId(signal) === A.normaliseId(planId)
      )
    ) {
      return true;
    }

    return A.planTextMatches(option.textContent, planId);
  };

  A.activateNativeControl = (control) => {
    if (!(control instanceof HTMLElement)) return;

    if ('disabled' in control) control.disabled = false;
    control.removeAttribute('disabled');

    try {
      control.click();
    } catch (_) {
      if (control instanceof HTMLInputElement) control.checked = true;
      control.dispatchEvent(new Event('input', { bubbles: true }));
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  A.syncAppstleCadenceControls = (planId) => {
    const root = A.state?.root;
    const desiredId = A.normaliseId(planId || '');
    if (!root?.isConnected || !desiredId) return;

    Array.from(root.querySelectorAll('select'))
      .filter(A.isAppstleControl)
      .forEach((select) => {
        const option = Array.from(select.options).find((candidate) =>
          A.optionMatchesPlan(candidate, desiredId)
        );
        if (!option) return;

        const changed = select.value !== option.value;
        select.disabled = false;
        select.removeAttribute('disabled');
        select.value = option.value;
        option.selected = true;
        select.dataset.femmeAppstleNativePlanState = 'subscription';

        if (changed) {
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

    Array.from(root.querySelectorAll('input[type="radio"]'))
      .filter((radio) => {
        if (!A.isAppstleControl(radio)) return false;
        if (radio.name === 'selling_plan') return false;
        return A.controlMatchesPlan(radio, desiredId);
      })
      .forEach((radio) => {
        radio.dataset.femmeAppstleNativePlanState = 'subscription';
        if (!radio.checked) A.activateNativeControl(radio);
      });
  };

  A.syncAppstleWidgetSelection = (planId) => {
    const root = A.state?.root;
    if (!root?.isConnected) return;

    const desiredId = A.normaliseId(planId || '');
    const radios = Array.from(
      root.querySelectorAll(
        'input[type="radio"][name="selling_plan"]:not([data-femme-appstle-plan="true"])'
      )
    ).filter(A.isAppstleSellingPlanRadio);

    if (!radios.length) {
      if (desiredId) A.syncAppstleCadenceControls(desiredId);
      return;
    }

    radios.forEach((radio) => {
      radio.disabled = false;
      radio.removeAttribute('disabled');
      radio.dataset.femmeAppstleNativePlanState = desiredId
        ? 'subscription'
        : 'one_time';
    });

    if (!desiredId) {
      const oneTimeTargets = radios.filter(
        (radio) => !A.normaliseId(radio.value || '')
      );

      oneTimeTargets.forEach((target) => {
        if (!target.checked) A.activateNativeControl(target);
      });
      return;
    }

    const exactTargets = radios.filter((radio) =>
      A.controlMatchesPlan(radio, desiredId)
    );

    if (exactTargets.length) {
      exactTargets.forEach((target) => {
        if (!target.checked) A.activateNativeControl(target);
      });
    } else {
      // Some newer Appstle layouts use one generic subscription radio and a
      // separate cadence selector. Activate subscription mode first, then set
      // that cadence selector to the exact femme-selected plan below.
      const subscriptionTarget =
        radios.find(
          (radio) =>
            radio.checked && Boolean(A.normaliseId(radio.value || ''))
        ) ||
        radios.find((radio) => Boolean(A.normaliseId(radio.value || '')));

      if (subscriptionTarget && !subscriptionTarget.checked) {
        A.activateNativeControl(subscriptionTarget);
      }
    }

    A.syncAppstleCadenceControls(desiredId);
  };

  A.syncNativePlanInputs = () => {
    const root = A.state?.root;
    if (!root?.isConnected) return;

    const isSubscription = A.state.selectionType === 'subscription';
    const planId = isSubscription
      ? A.normaliseId(A.state.selectedPlanId || '')
      : '';

    // Appstle's add.js interceptor reads its own hidden purchase-option state.
    // Keep that native state aligned with the visible femme choice first.
    A.syncAppstleWidgetSelection(planId);

    root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form
        .querySelectorAll(
          '[name="selling_plan"]:not([data-femme-appstle-plan="true"])'
        )
        .forEach((control) => {
          const stateName = planId ? 'subscription' : 'one_time';
          control.dataset.femmeAppstleNativePlanState = stateName;

          if (A.isAppstleSellingPlanRadio(control)) {
            // Never rewrite Appstle radio values. Their native values are part
            // of Appstle's own purchase-mode state.
            control.disabled = false;
            control.removeAttribute('disabled');
            return;
          }

          if (
            control instanceof HTMLInputElement &&
            String(control.type || '').toLowerCase() === 'radio'
          ) {
            // A competing/legacy selling-plan radio must not participate in the
            // submitted product form while Appstle owns storefront subscriptions.
            control.disabled = true;
            control.setAttribute('disabled', 'disabled');
            return;
          }

          if (control instanceof HTMLSelectElement) {
            const desiredValue = planId || '';
            const option = Array.from(control.options).find((candidate) =>
              desiredValue
                ? A.optionMatchesPlan(candidate, desiredValue)
                : !A.normaliseId(candidate.value || '')
            );

            if (option) {
              control.disabled = false;
              control.removeAttribute('disabled');
              control.value = option.value;
              option.selected = true;
            } else {
              control.disabled = true;
              control.setAttribute('disabled', 'disabled');
            }
            return;
          }

          control.disabled = false;
          control.removeAttribute('disabled');
          if ('value' in control) control.value = planId;
          control.setAttribute('value', planId);
        });
    });
  };

  A.scheduleNativePlanSync = () => {
    [0, 60, 180].forEach((delay) => {
      window.setTimeout(A.syncNativePlanInputs, delay);
    });
  };

  document.addEventListener(
    'fsc:purchase-option-changed',
    A.scheduleNativePlanSync
  );

  window.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !A.state?.root) return;

      const purchaseControl = target.closest(
        'button.product-form__submit[name="add"],button[name="add"],input[name="add"],form[action*="/cart/add"] [type="submit"]'
      );

      if (!purchaseControl || !A.state.root.contains(purchaseControl)) return;
      A.syncNativePlanInputs();
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (
        !(form instanceof HTMLFormElement) ||
        !A.state?.root?.contains(form) ||
        !String(form.action || '').includes('/cart/add')
      ) {
        return;
      }

      A.syncNativePlanInputs();
    },
    true
  );

  A.parseJson = (el) => {
    try {
      return el ? JSON.parse(el.textContent || '{}') : null;
    } catch (error) {
      console.error('[femme Appstle] Invalid JSON.', error);
      return null;
    }
  };

  A.money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  A.normaliseId = (value) =>
    String(value || '').replace(/^gid:\/\/shopify\/[^/]+\//, '');

  A.planDisplayLabel = (plan) => {
    const id = A.normaliseId(plan?.id);
    if (A.PLAN_LABELS[id]) return A.PLAN_LABELS[id];

    const name = String(plan?.name || '').trim();
    if (/fortnight/i.test(name)) return 'Every 2 weeks';

    const match = name.match(/^(\d+)\s*Monthly/i);
    if (match) {
      const months = Number(match[1]);
      return months === 1 ? 'Every month' : `Every ${months} months`;
    }

    return name || 'Subscription';
  };

  A.deliveryBenefit = (plan) => {
    const label = A.planDisplayLabel(plan);
    if (label === 'Every month') return 'Delivers monthly';
    if (/^Every\s+/i.test(label)) return `Delivers ${label.toLowerCase()}`;
    return `Delivery: ${label}`;
  };

  A.defaultPlan = (plans = []) => {
    if (!Array.isArray(plans) || !plans.length) return null;
    return (
      plans.find((plan) => A.normaliseId(plan.id) === A.DEFAULT_PLAN_ID) ||
      plans.find((plan) => /(^|\s)1\s*Monthly/i.test(String(plan.name || ''))) ||
      plans[0]
    );
  };

  A.productUrl = (value) => {
    const raw = String(value || window.location.pathname);
    const source = raw.startsWith('/') ? raw : `/products/${raw}`;
    const url = new URL(source, window.location.origin);
    url.hash = '';
    url.search = '';
    let path = url.pathname.replace(/\/$/, '');
    if (!path.endsWith('.js')) path += '.js';
    return `${url.origin}${path}`;
  };

  A.fetchProduct = async (value) => {
    const url = A.productUrl(value);
    if (A.cache.has(url)) return A.cache.get(url);

    const request = fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product JSON ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        A.cache.delete(url);
        throw error;
      });

    A.cache.set(url, request);
    return request;
  };

  A.appstleData = (product) => {
    const allGroups = Array.isArray(product?.selling_plan_groups)
      ? product.selling_plan_groups
      : [];

    const targetName = A.TARGET_GROUP_NAME.toLowerCase();
    const groups = allGroups.filter((group) => {
      const appId = String(group.app_id || group.appId || '').toLowerCase();
      const groupId = A.normaliseId(group.id);
      const groupName = String(
        group.name || group.merchant_code || group.merchantCode || ''
      ).trim().toLowerCase();

      return (
        appId.includes(A.APP_ID) &&
        (groupId === A.TARGET_GROUP_ID || groupName === targetName)
      );
    });

    const plans = new Map();
    groups.forEach((group) => {
      (group.selling_plans || []).forEach((plan) => {
        const id = A.normaliseId(plan.id);
        plans.set(id, {
          id,
          name: String(plan.name || ''),
          groupName: String(group.name || A.TARGET_GROUP_NAME),
          appId: String(group.app_id || group.appId || ''),
          displayLabel: A.PLAN_LABELS[id] || ''
        });
      });
    });

    const variants = new Map();
    (product?.variants || []).forEach((variant) => {
      const variantPlans = (variant.selling_plan_allocations || [])
        .map((allocation) => {
          const plan = plans.get(A.normaliseId(allocation.selling_plan_id));
          return plan
            ? {
                ...plan,
                price: Number(allocation.price || 0),
                compareAt: Number(
                  allocation.compare_at_price || variant.price || 0
                )
              }
            : null;
        })
        .filter(Boolean);

      variants.set(String(variant.id), {
        id: String(variant.id),
        price: Number(variant.price || 0),
        plans: variantPlans
      });
    });

    return variants;
  };
})();